import { useEventResolutionEnabled } from "@/features/events/hooks/useEventResolutionEnabled";
import Icon from "@imminently/decisively-ui/Icon";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { useState } from "react";

import { tasksService } from "@/app/providers/services";
import { ProjectWriterGuard } from "@/auth/ComponentGuard";
import { EmptyContent } from "@/common/EmptyContent";
import { ResourceLoading } from "@/common/ResourceCard";
import { formatIgnoreTimezone } from "@/common/util";
import { useScrollable } from "@/hooks/use-scrollable";
import { useScope } from "@/redux/scope";
import { colors } from "@/theme";
import { AssigneeGroup } from "@/ui/forms/assignee-group";
import Button from "@imminently/decisively-ui/Button";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Label from "@imminently/decisively-ui/Label";
import type { Sort } from "@imminently/immi-query";
import { Tooltip, Typography } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Drawer from "@mui/material/Drawer";
import type { Task } from "@packages/commons";
import clsx from "clsx";
import Select from "react-select";
import { PriorityLabel, StatusLabel } from "./TaskComponents";
import { useTaskModal } from "./TaskModal";
import { type SelectOption, sortOptions, statusOptions, validateTask } from "./taskUtils";
import styles from "./tasks.module.scss";

const TaskCard = ({ task, onClick }: { task: Task; onClick: (task: Task) => void }) => {
  return (
    <Card
      className={styles.task}
      onClick={() => onClick(task)}
    >
      <CardHeader
        className={styles.test}
        title={task.title}
      />
      <CardContent className={styles.taskContent}>
        <div className={styles.taskRow}>
          <StatusLabel status={task.status} />
          <PriorityLabel priority={task.priority} />
          <Tooltip
            title="Due Date"
            placement="left"
          >
            <Typography>{formatIgnoreTimezone(task.dueDate, "MMM DD, YYYY", undefined)}</Typography>
          </Tooltip>
        </div>
        <div className={styles.taskRow}>
          {task.tags.map((tag) => (
            <Label
              key={tag}
              className={styles.label}
              color={colors.subtleGrey}
            >
              {tag}
            </Label>
          ))}
          <AssigneeGroup users={task.assigneesData} />
        </div>
      </CardContent>
    </Card>
  );
};

const SortBy = ({ value, onClick }: { value: SelectOption; onClick: (option: SelectOption) => void }) => {
  return (
    <>
      <Tooltip title="Sort By">
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button
              label={value.label}
              variant="outline"
              intent="secondary"
              className={styles.sort}
            >
              <Icon name="sort" />
              {value.label}
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end">
            {sortOptions.map((option) => (
              <DropdownMenu.Item
                key={option.value}
                onClick={() => onClick(option)}
              >
                <DropdownMenu.Item.Label>{option.label}</DropdownMenu.Item.Label>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu>
      </Tooltip>
    </>
  );
};

export const TaskPanel = ({ open, setOpen }: any) => {
  const { workspace } = useScope();
  const eventResolutionEnabled = useEventResolutionEnabled();
  const [filter, setFilter] = useState<SelectOption>();
  const [sort, setSort] = useState<SelectOption>(sortOptions[0]);
  const openTaskModal = useTaskModal();

  const {
    data: tasks,
    total,
    query,
    loadMore,
  } = tasksService.useInfiniteResources({
    perPage: 10,
    filter: {
      type: eventResolutionEnabled ? ["rule", "event"] : "rule",
      sort: { field: sort.value, order: "asc" } as Sort,
      status: filter?.value,
      workspace,
    },
    enabled: open,
  });

  const { isLoading } = query;
  const canLoad = !query.isError && !query.isFetching && (query.hasNextPage ?? false);
  const scrollLoad = useScrollable(loadMore, canLoad, { autoScroll: true });

  // a react mui side drawer that opens on the right side of the screen
  // contains a list of tasks
  // each task has a title, description, and a button to mark as complete

  const addTask = () => {
    // open create task modal
    openTaskModal();
    setOpen(false);
  };

  const openTask = (task: Task) => {
    // open task modal
    openTaskModal(task);
    setOpen(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
    >
      <div
        className={styles.header}
        style={{ minHeight: "4rem" }}
      >
        <Typography variant="h3">Tasks</Typography>
        <ProjectWriterGuard>
          <Button
            onClick={addTask}
            label="Add"
          >
            Add
          </Button>
        </ProjectWriterGuard>
      </div>
      <div className={styles.controls}>
        <Select
          className={styles.filter}
          options={statusOptions}
          value={filter}
          onChange={(e) => setFilter(e as SelectOption)}
          placeholder="Filter by status"
          isClearable
        />
        <SortBy
          value={sort}
          onClick={setSort}
        />
      </div>
      <div
        {...scrollLoad}
        className={clsx(styles.content, "scrollable", styles.minWidth)}
      >
        {isLoading ? (
          <LoadingSpinner size="sm" />
        ) : !isLoading && total === 0 ? (
          <EmptyContent messages={["No tasks"]} />
        ) : (
          <>
            <Typography variant="h6">Tasks • {total}</Typography>
            {tasks.map(validateTask).map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={openTask}
              />
            ))}
            {query.isFetchingNextPage ? <ResourceLoading width="400px" /> : null}
          </>
        )}
      </div>
    </Drawer>
  );
};

export const TaskButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        icon
        label="Open tasks"
        variant="ghost"
        intent="sidebar"
        onClick={() => setOpen(true)}
      >
        <Icon name="tasks" />
      </Button>
      <TaskPanel
        open={open}
        setOpen={setOpen}
      />
    </>
  );
};
